import { useState } from 'react';
import { line, Color, Size, t } from '@bms2/shared';
import { Button, Select, TextInput } from '../../components/controls';
import styles from './RecordPage.module.css';
import { post, useApi } from './api';

/** 🔨 Relative to the API's own root, never prefixed with /api (piece 1). */
export const emergencyConfirmationPath = (workOrderId: string): string => `/work/emergency/${workOrderId}/confirmation`;
export const emergencyContactsPath = (workOrderId: string): string => `/work/emergency/${workOrderId}/contacts`;
export const emergencyPreviewPath = (workOrderId: string, contactEmploymentId: string): string => `/work/emergency/${workOrderId}/preview?contactEmploymentId=${encodeURIComponent(contactEmploymentId)}`;
export const INBOUND_REPLY_PATH = '/work/messages/inbound';

interface Preview {
  contact: { employmentId: string; name: string; title: string | null; number: string | null } | null;
  body: string;
  problem: string | null;
}

interface State {
  sent: { id: string; toNumber: string; body: string; status: string; statusAt: string }[];
  replies: { id: string; fromNumber: string | null; body: string; receivedAt: string; affirmative: boolean }[];
  confirmed: boolean;
}

/**
 * 🔒 The customer's text confirmation of an emergency (piece 29). The screen
 * names the person texted and their title, and shows the message word for
 * word, before anything is sent; a message missing either figure is refused
 * here and at the server. 🔨 The job is already dispatched: no reply, an
 * undelivered text and an Unknown status hold nothing. A reply received is
 * filed against the number it came from and matched to the message it
 * answers; one that matches nothing authorises nothing.
 */
export function WorkOrderConfirmation({ workOrderId, editable, apiBaseUrl = '', onError }: { workOrderId: string; editable: boolean; apiBaseUrl?: string; onError: (message: string) => void }) {
  const contacts = useApi<{ rows: { employmentId: string; name: string; title: string | null; number: string | null }[] }>(emergencyContactsPath(workOrderId), apiBaseUrl);
  const state = useApi<State>(emergencyConfirmationPath(workOrderId), apiBaseUrl);
  const [contactId, setContactId] = useState('');
  const preview = useApi<Preview>(contactId ? emergencyPreviewPath(workOrderId, contactId) : null, apiBaseUrl);
  const [reply, setReply] = useState<{ fromNumber: string; body: string } | null>(null);
  const rows = contacts.state.kind === 'ready' && Array.isArray(contacts.state.data?.rows) ? contacts.state.data.rows : [];
  const shown = preview.state.kind === 'ready' ? preview.state.data : null;
  const current = state.state.kind === 'ready' && state.state.data && Array.isArray(state.state.data.sent) ? state.state.data : null;

  const send = async () => {
    const wrote = await post(emergencyConfirmationPath(workOrderId), { contactEmploymentId: contactId }, apiBaseUrl);
    if (!wrote.ok) return onError(wrote.message);
    state.reload?.();
  };
  const file = async () => {
    if (!reply) return;
    const wrote = await post(INBOUND_REPLY_PATH, { fromNumber: reply.fromNumber, body: reply.body }, apiBaseUrl);
    if (!wrote.ok) return onError(wrote.message);
    setReply(null);
    state.reload?.();
  };

  return (
    <div data-work-order-confirmation={workOrderId} data-confirmed={current?.confirmed ? 'true' : 'false'}>
      <p className={styles.sectionTitle}>{t('emergency.confirmation.title')}</p>
      {current?.confirmed ? <p className={styles.hint} data-confirmation-yes="true">{t('emergency.confirmation.confirmed')}</p> : <p className={styles.hint}>{t('emergency.confirmation.hint')}</p>}
      {editable ? (
        <div className={styles.actions}>
          <Select size={Size.Medium} label={t('emergency.confirmation.contact')} value={contactId} onChange={setContactId} placeholder={t('workOrder.new.noneYet')} options={rows.map((row) => ({ value: row.employmentId, label: line(row.name, row.title ? `· ${row.title}` : '', row.number ? `· ${row.number}` : `· ${t('emergency.confirmation.noNumber')}`) }))} dataAttribute={{ 'data-confirmation-contact': 'true' }} />
        </div>
      ) : null}
      {shown ? (
        <div data-confirmation-preview="true">
          {shown.contact ? <p className={styles.hint} data-confirmation-to={shown.contact.number ?? ''}>{line(t('emergency.confirmation.to'), shown.contact.name, shown.contact.title ? `· ${shown.contact.title}` : '', shown.contact.number ? `· ${shown.contact.number}` : '')}</p> : null}
          <p className={styles.hint} data-confirmation-body="true">{shown.body}</p>
          {shown.problem ? <p className={styles.problem} data-confirmation-problem="true">{shown.problem}</p> : null}
          {editable ? <Button size={Size.Small} color={Color.Navy} text={[t('emergency.confirmation.send')]} actions={[() => void send()]} disabled={shown.problem !== null} disabledReason={shown.problem ?? ''} /> : null}
        </div>
      ) : null}
      <ul className={styles.plainList} data-confirmation-history="true">
        {(current?.sent ?? []).map((row) => <li key={row.id} className={styles.hint} data-confirmation-sent={row.status}>{line(t('emergency.confirmation.sent'), row.toNumber, '·', row.status)}</li>)}
        {(current?.replies ?? []).map((row) => <li key={row.id} className={row.affirmative ? styles.hint : styles.problem} data-confirmation-reply={row.affirmative ? 'yes' : 'other'}>{line(t('emergency.confirmation.reply'), row.fromNumber ?? '', '·', `“${row.body}”`)}</li>)}
      </ul>
      {editable ? (
        reply ? (
          <div className={styles.actions} data-confirmation-file-reply="true">
            <TextInput size={Size.Medium} label={t('emergency.confirmation.fromNumber')} value={reply.fromNumber} onChange={(value) => setReply({ ...reply, fromNumber: value })} name="reply-from" required />
            <TextInput size={Size.Large} label={t('emergency.confirmation.replyText')} value={reply.body} onChange={(value) => setReply({ ...reply, body: value })} name="reply-body" required />
            <Button size={Size.Small} text={[t('emergency.confirmation.file')]} actions={[() => void file()]} disabled={!reply.fromNumber.trim() || !reply.body.trim()} disabledReason={t('emergency.confirmation.fileNeeds')} />
            <Button size={Size.Small} color={Color.Inactive} text={[t('modal.cancel')]} actions={[() => setReply(null)]} />
          </div>
        ) : (
          <Button size={Size.Small} color={Color.Inactive} text={[t('emergency.confirmation.recordReply')]} actions={[() => setReply({ fromNumber: '', body: '' })]} />
        )
      ) : null}
    </div>
  );
}
